// src/components/TripsCard.js
//
// Dashboard card for trips (pairings): what's in progress or coming up, and
// what was flown recently. Each trip shows its dates, the airports it
// touches, leg count and block time; expanding it lists the legs by duty
// day. Flights that aren't part of a trip are left to the flight lists.

import { useState } from 'react';
import { Briefcase, ChevronDown, ChevronUp, ArrowRight, Hotel } from 'lucide-react';
import { format, differenceInCalendarDays } from 'date-fns';

// A trip whose legs are more than this many days apart is treated as
// separate pairings — trip numbers get reused from one bid month to the next
const MAX_GAP_DAYS = 3;
const TRIPS_SHOWN = 4;

// "2026-10-21" or "2026-10-21T05:00:00.000Z" -> local midnight on that date
const parseDate = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).split('T')[0].split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

// "0745", "07:45" or a datetime -> minutes after midnight, for ordering legs
const timeKey = (value) => {
  if (!value) return 0;
  const timePart = String(value).split(/[T ]/).pop();
  const digits = timePart.replace(/\D/g, '').padStart(4, '0').slice(0, 4);
  return Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2));
};

const formatClock = (value) => {
  if (!value) return null;
  const minutes = timeKey(value);
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}${String(minutes % 60).padStart(2, '0')}`;
};

// Logged time once flown, scheduled block before that
const legHours = (flight) => {
  const total = parseFloat(flight.total_time);
  if (total > 0) return total;
  return parseFloat(flight.scheduled_block) || 0;
};

const formatHours = (hours) => `${hours.toFixed(1)}h`;

const formatRange = (start, end) => {
  if (differenceInCalendarDays(end, start) === 0) return format(start, 'EEE, MMM d');
  if (start.getMonth() === end.getMonth()) return `${format(start, 'MMM d')} – ${format(end, 'd')}`;
  return `${format(start, 'MMM d')} – ${format(end, 'MMM d')}`;
};

// Airports in the order they're visited, without repeating a stop the
// previous leg already ended at
const routeOf = (legs) => {
  const stops = [];
  legs.forEach((leg) => {
    [leg.departure_airport, leg.arrival_airport].forEach((airport) => {
      if (airport && stops[stops.length - 1] !== airport) stops.push(airport);
    });
  });
  return stops;
};

const sortLegs = (flights) =>
  flights
    .map((flight) => ({ flight, date: parseDate(flight.date) }))
    .filter(({ date }) => date)
    .sort((a, b) => (a.date - b.date) || (timeKey(a.flight.scheduled_out || a.flight.actual_out) - timeKey(b.flight.scheduled_out || b.flight.actual_out)));

// Turn API trips into display trips: legs sorted, long gaps split, and each
// classified against today as in progress, upcoming or completed
export const buildTrips = (trips, today = new Date()) => {
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const result = [];

  trips.forEach((trip) => {
    const segments = [];
    sortLegs(trip.flights || []).forEach((leg) => {
      const current = segments[segments.length - 1];
      if (current && differenceInCalendarDays(leg.date, current[current.length - 1].date) <= MAX_GAP_DAYS) {
        current.push(leg);
      } else {
        segments.push([leg]);
      }
    });

    segments.forEach((legs, index) => {
      const start = legs[0].date;
      const end = legs[legs.length - 1].date;
      let status = 'completed';
      if (start > todayDate) status = 'upcoming';
      else if (end >= todayDate) status = 'inProgress';

      result.push({
        key: segments.length > 1 ? `${trip.id}-${index}` : trip.id,
        tripNumber: trip.trip_number,
        hotelName: trip.hotel_name,
        start,
        end,
        days: differenceInCalendarDays(end, start) + 1,
        dayOfTrip: differenceInCalendarDays(todayDate, start) + 1,
        startsIn: differenceInCalendarDays(start, todayDate),
        endedAgo: differenceInCalendarDays(todayDate, end),
        legs,
        route: routeOf(legs.map(({ flight }) => flight)),
        hours: legs.reduce((sum, { flight }) => sum + legHours(flight), 0),
        status,
      });
    });
  });

  const upcoming = result
    .filter((trip) => trip.status !== 'completed')
    .sort((a, b) => a.start - b.start);
  const recent = result
    .filter((trip) => trip.status === 'completed')
    .sort((a, b) => b.end - a.end);
  return { upcoming, recent };
};

const statusLabel = (trip) => {
  if (trip.status === 'inProgress') return `Day ${trip.dayOfTrip} of ${trip.days}`;
  if (trip.status === 'upcoming') {
    if (trip.startsIn === 1) return 'Tomorrow';
    return `In ${trip.startsIn} days`;
  }
  if (trip.endedAgo === 1) return 'Yesterday';
  return `${trip.endedAgo} days ago`;
};

const STATUS_STYLES = {
  inProgress: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  upcoming: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
  completed: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
};

// Long routes keep the first and last few stops
const RouteLine = ({ stops }) => {
  const shown = stops.length > 7 ? [...stops.slice(0, 3), '…', ...stops.slice(-3)] : stops;
  return (
    <div className="flex flex-wrap items-center gap-x-1 text-sm font-medium text-gray-700">
      {shown.map((stop, index) => (
        <span key={`${stop}-${index}`} className="flex items-center">
          {index > 0 && <ArrowRight className="h-3 w-3 mr-1 text-gray-400" aria-hidden="true" />}
          {stop}
        </span>
      ))}
    </div>
  );
};

const LegRow = ({ flight, onFlightClick }) => {
  const out = formatClock(flight.actual_out || flight.scheduled_out);
  return (
    <button
      type="button"
      onClick={() => onFlightClick?.(flight)}
      className="w-full flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 text-left shadow-sm hover:bg-sky-50 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="w-12 shrink-0 font-mono text-xs text-gray-500">{out || '—'}</span>
        <span className="text-sm font-medium text-gray-900 whitespace-nowrap">
          {flight.departure_airport} <ArrowRight className="inline h-3 w-3 text-gray-400" aria-hidden="true" /> {flight.arrival_airport}
        </span>
        {flight.flight_number && (
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded truncate">{flight.flight_number}</span>
        )}
        {flight.aircraft_id && (
          <span className="hidden sm:inline text-xs text-gray-500 truncate">{flight.aircraft_id}</span>
        )}
      </div>
      <span className="shrink-0 text-sm font-semibold text-sky-700">{formatHours(legHours(flight))}</span>
    </button>
  );
};

const TripRow = ({ trip, isExpanded, onToggle, onFlightClick }) => {
  // Legs grouped by calendar day for the expanded view
  const days = [];
  trip.legs.forEach(({ flight, date }) => {
    const last = days[days.length - 1];
    if (last && last.date.getTime() === date.getTime()) last.flights.push(flight);
    else days.push({ date, flights: [flight] });
  });

  return (
    <div className={`rounded-xl border overflow-hidden ${trip.status === 'inProgress' ? 'border-green-200' : 'border-gray-100'}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="w-full p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">Trip {trip.tripNumber}</span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[trip.status]}`}>
                {statusLabel(trip)}
              </span>
            </div>
            <RouteLine stops={trip.route} />
            <div className="flex flex-wrap items-center gap-x-3 text-xs text-gray-500">
              <span>{formatRange(trip.start, trip.end)}</span>
              <span>{trip.days} day{trip.days === 1 ? '' : 's'}</span>
              <span>{trip.legs.length} leg{trip.legs.length === 1 ? '' : 's'}</span>
              {trip.hotelName && (
                <span className="flex items-center">
                  <Hotel className="h-3 w-3 mr-1" aria-hidden="true" />
                  {trip.hotelName}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm font-bold text-sky-700">{formatHours(trip.hours)}</span>
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
            )}
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-3">
          {days.map((day) => (
            <div key={day.date.getTime()}>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Day {differenceInCalendarDays(day.date, trip.start) + 1} · {format(day.date, 'EEE, MMM d')}
              </div>
              <div className="space-y-1">
                {day.flights.map((flight, index) => (
                  <LegRow key={flight.id || index} flight={flight} onFlightClick={onFlightClick} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TripsCard = ({ trips = [], onFlightClick, today }) => {
  const { upcoming, recent } = buildTrips(trips, today);
  const [tab, setTab] = useState(upcoming.length > 0 || recent.length === 0 ? 'upcoming' : 'recent');
  const [expandedKey, setExpandedKey] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const list = tab === 'upcoming' ? upcoming : recent;
  const shown = showAll ? list : list.slice(0, TRIPS_SHOWN);

  const selectTab = (next) => {
    setTab(next);
    setShowAll(false);
    setExpandedKey(null);
  };

  const tabs = [
    { id: 'upcoming', label: 'Upcoming', count: upcoming.length },
    { id: 'recent', label: 'Recent', count: recent.length },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-bold text-gray-900 flex items-center">
          <Briefcase className="h-6 w-6 mr-2 text-sky-600" />
          Trips
        </h2>
        <div role="tablist" aria-label="Trips" className="inline-flex rounded-lg bg-gray-100 p-1">
          {tabs.map(({ id, label, count }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => selectTab(id)}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                tab === id ? 'bg-white text-sky-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {label} <span className="text-xs text-gray-400">{count}</span>
            </button>
          ))}
        </div>
      </div>

      {list.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500">
          {tab === 'upcoming' ? 'No trips scheduled' : 'No trips in the last 60 days'}
        </p>
      ) : (
        <div className="space-y-3">
          {shown.map((trip) => (
            <TripRow
              key={trip.key}
              trip={trip}
              isExpanded={expandedKey === trip.key}
              onToggle={() => setExpandedKey(expandedKey === trip.key ? null : trip.key)}
              onFlightClick={onFlightClick}
            />
          ))}
        </div>
      )}

      {list.length > TRIPS_SHOWN && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-sky-600 hover:text-sky-800 font-medium"
          >
            {showAll ? 'Show fewer trips' : `Show ${list.length - TRIPS_SHOWN} more trip${list.length - TRIPS_SHOWN === 1 ? '' : 's'}`}
          </button>
        </div>
      )}
    </div>
  );
};

export default TripsCard;
